import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FoldersService } from '../../../services/Folders/folders.service';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { Folder } from '../../../modules/projects/projects-api-models';

@Component({
  selector: 'app-multiselect-labels-dropdown',
  templateUrl: './multiselect-labels-dropdown.component.html',
  styleUrls: ['./multiselect-labels-dropdown.component.scss'],
})
export class MultiselectLabelsDropdownComponent implements OnInit, OnChanges {
  @Input() labels: any[];
  @Input() enableMatLabel: boolean = false;
  @Output() labelsData: EventEmitter<{ selectedLabels: string[]; unselectedLabels: string[] }> =
    new EventEmitter<{
      selectedLabels: string[];
      unselectedLabels: string[];
    }>();
  @Input() MAX_SELECTION: number = 10;
  @Input() resetData: boolean = false;
  @Input() filterData: boolean = false;
  mainSelection: any[] = [];
  selectedLabels: any[] = [];
  loading = false;
  protected _onDestroy = new Subject<void>();
  folderSearch = new FormControl('');
  compareLabels = (a: any, b: any) => a?.id === b?.id;
  @ViewChild('labelSelect', { static: false }) labelSelect: any;
  selectedLabel: any = null;
  toggleSelectOpened: boolean = false;
  nonExistingSelectedLabels: any[] = [];

  constructor(
    private foldersService: FoldersService,
    public translateService: TranslocoService,
    private cdRef: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    if (!this.labels || this.labels.length === 0) {
      await this.getLabels();
    }
    this.subscribeToFolderSearchChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.resetData) {
      this.mainSelection = [];
      this.selectedLabels = [];
    }
  }

  async getLabels(pageNo: number = 1, searchText?: string) {
    this.loading = true;
    await this.foldersService.getFolders(20, pageNo, searchText);
    this.labels =
      pageNo === 1
        ? this.foldersService.folderList
        : this.labels.concat(this.foldersService.folderList);
    this.loading = false;
    if (searchText) {
      this.labels = this.buildNestedStructure(this.labels);
    } else {
      // this.getSelectedLabels();
    }
  }

  async onScroll() {
    if (
      this.foldersService.folderMeta.current_page < this.foldersService.folderMeta.total_pages &&
      !this.loading
    ) {
      await this.getLabels(this.foldersService.folderMeta.current_page + 1);
    }
  }

  onIconClick(event: MouseEvent, label: any): void {
    event.stopPropagation(); // prevent selection
    this.selectedLabel = label;
    label.sub_folders.forEach((children: any) => {
      children.show = !children.show;
    });
    this.isUnselected({ selected: !!label.selected }, label);
    this.cdRef.detectChanges();
  }

  // Subscribe to changes in the folder search input
  subscribeToFolderSearchChanges(): void {
    this.folderSearch.valueChanges
      .pipe(
        takeUntil(this._onDestroy),
        startWith(''), // Include an initial empty string
        debounceTime(500), // Wait for 300 ms before emitting value to avoid too many API calls
      )
      .subscribe(async (value) => {
        if (value.trimStart()) {
          // this.folderSearch.disable();
          await this.getLabels(1, value); // Update the list of filtered folders based on search term
          // this.folderSearch.enable();
        } else if (value.toString().length === 0) {
          await this.getLabels(1, value);
        }
      });
  }

  isUnselected(matOption, label) {
    if (matOption.selected) {
      setTimeout(() => {
        label.selected = true;
        this.selectedLabels?.push(label);
        if (label.sub_folders?.length > 0 && this.filterData) {
          label.sub_folders.forEach((sub_label: any) => {
            if (
              this.selectedLabels?.length < this.MAX_SELECTION &&
              typeof sub_label.show === 'boolean'
            ) {
              sub_label.selected = true;
              this.selectedLabels?.push(sub_label);
            }
          });
        }
        this.selectedLabels = this.selectedLabels?.filter(
          (value, index, self) => index === self.findIndex((v) => v.id === value.id),
        );
        this.cdRef.detectChanges();
        this.mainSelection = this.selectedLabels;
      }, 100);
    } else {
      label.selected = false;
      const deselected = this.selectedLabels?.findIndex((pro) => pro.id === label.id);
      deselected !== -1 ? this.selectedLabels?.splice(deselected, 1) : null;
      this.cdRef.detectChanges();
      this.mainSelection = this.selectedLabels;
    }

    setTimeout(() => {
      if (this.filterData) {
        this.foldersService.appliedFilter = this.selectedLabels;
      }
      this.labelsData.emit({
        selectedLabels: this.selectedLabels?.map((label) => label.id),
        unselectedLabels: this.getUnselectedItemsFlat(this.labels, this.selectedLabels),
      });
    }, 150);
  }

  getUnselectedItemsFlat(allItems: Folder[], selectedItems: Folder[]): string[] {
    const selectedIds = new Set(selectedItems.map((item) => item.id));

    const unselected: any[] = [];

    for (const parent of allItems) {
      if (!selectedIds.has(parent.id)) {
        unselected.push(parent);
      }

      if (Array.isArray(parent.sub_folders)) {
        for (const child of parent.sub_folders) {
          if (!selectedIds.has(child.id)) {
            unselected.push(child);
          }
        }
      }
    }

    return unselected.map((label) => label.id);
  }

  // getSelectedLabels() {
  //   const allExistingIds = new Set<number>();
  //
  //   // Mark `selected = true` in top-level labels and their sub_folders
  //   this.labels?.forEach((label) => {
  //     const isParentSelected = this.selectedLabels?.some((sel) => sel.id === label.id);
  //     label.selected = !!isParentSelected;
  //     allExistingIds.add(label.id);
  //
  //     // Handle children
  //     label.sub_folders?.forEach((child) => {
  //       const isChildSelected = this.selectedLabels?.some((sel) => sel.id === child.id);
  //       child.selected = !!isChildSelected;
  //       allExistingIds.add(child.id);
  //     });
  //   });
  //
  //   const unmatched = this.selectedLabels?.filter((item) => !allExistingIds.has(item.id)) || [];
  //
  //   const nonExistingSelectedMap = new Map(unmatched.map((item) => [item.id, item]));
  //   const groupedChildren = new Set<number>();
  //
  //   // Group children under their selected parents
  //   unmatched.forEach((item) => {
  //     if (item.parent_id && nonExistingSelectedMap.has(item.parent_id)) {
  //       const parent = nonExistingSelectedMap.get(item.parent_id);
  //
  //       if (!parent.sub_folders) {
  //         parent.sub_folders = [];
  //       }
  //
  //       const existingChildIndex = parent.sub_folders.findIndex((c) => c.id === item.id);
  //
  //       if (existingChildIndex !== -1) {
  //         // ✅ Replace existing child
  //         parent.sub_folders[existingChildIndex] = { ...item, selected: true };
  //       } else {
  //         // ✅ Add new child
  //         parent.sub_folders.push({ ...item, selected: true });
  //       }
  //
  //       groupedChildren.add(item.id);
  //     }
  //   });
  //
  //   // Final list: parents (with their grouped children) + standalone items (not grouped as children)
  //   const nestedNonExistingLabels: any[] = [];
  //
  //   unmatched.forEach((item) => {
  //     const isGroupedChild = groupedChildren.has(item.id);
  //     const isParent = item.parent_id === null;
  //
  //     if (!isGroupedChild || isParent) {
  //       nestedNonExistingLabels.push({ ...item, selected: true });
  //     }
  //   });
  //
  //   this.nonExistingSelectedLabels = nestedNonExistingLabels || [];
  // }

  buildNestedStructure(flatList: any[]): any[] {
    const map = new Map<number, any>();
    const roots: any[] = [];

    flatList.forEach((item) => {
      map.set(item.id, { ...item, sub_folders: item.sub_folders || [] });
    });

    map.forEach((item) => {
      //means it's a parent label
      if (item.parent_id === null) {
        roots.push(item);
      } else {
        const parent = map.get(item.parent_id);
        // check if the parent is also in the list
        if (parent) {
          item.show = true;
          parent.sub_folders.push(item);
        } else {
          // if no parent is found, just push it in the list.
          roots.push(item);
        }
      }
    });

    return roots;
  }

  getLabelsList() {
    const list = structuredClone(this.selectedLabels.map((label) => label.folder_name));
    return list.length > 0 ? list.join('\n') : null;
  }
}
